import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Sweep } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Search, Loader2, UserPlus, Star, X, Mail, Inbox } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const monthsAgo = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

export default function InboxSweepDialog({ open, onOpenChange, onAdded, onOpenProspect }) {
  const { activeShowId } = useApp();
  const [dateFrom, setDateFrom] = useState(monthsAgo(6));
  const [dateTo, setDateTo] = useState(today());
  const [sweep, setSweep] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const pollRef = useRef(null);

  const refresh = async () => {
    const [s, c] = await Promise.all([Sweep.status(), Sweep.candidates('new')]);
    setSweep(s);
    setCandidates(c);
    return s;
  };

  useEffect(() => {
    if (!open) { clearInterval(pollRef.current); return; }
    refresh().then(s => { if (s?.status === 'running') startPolling(); });
    return () => clearInterval(pollRef.current);
  }, [open]); // eslint-disable-line

  const startPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await refresh();
      if (s?.status !== 'running') {
        clearInterval(pollRef.current);
        if (s?.status === 'done') toast({ title: 'Sweep complete', description: `${s.found || 0} potential lead${s.found === 1 ? '' : 's'} found` });
        if (s?.status === 'error') toast({ title: 'Sweep failed', description: s.error || '' });
      }
    }, 2500);
  };

  const start = async () => {
    try {
      await Sweep.start(dateFrom, dateTo);
      toast({ title: 'Sweep started', description: 'Scanning inbox, sent items and all folders…' });
      await refresh();
      startPolling();
    } catch (e) {
      toast({ title: 'Could not start sweep', description: e?.response?.data?.detail || String(e) });
    }
  };

  const handle = async (cand, action) => {
    setBusyId(cand.id);
    try {
      if (action === 'dismiss') {
        await Sweep.dismiss(cand.id);
        toast({ title: 'Dismissed', description: `${cand.name} won't appear in future sweeps.` });
      } else {
        const addAs = action === 'email' ? 'prospect' : action;
        const r = await Sweep.add(cand.id, addAs, activeShowId);
        toast({
          title: addAs === 'potential' ? 'Added as Potential Guest' : 'Added as Prospect',
          description: action === 'email' ? 'Opening their card — use the “Reach-out letter” tab to pick a template.' : cand.name,
        });
        onAdded?.();
        if (action === 'email') {
          onOpenChange(false);
          onOpenProspect?.(r.id);
        }
      }
      setCandidates(prev => prev.filter(c => c.id !== cand.id));
    } catch (e) {
      toast({ title: 'Action failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyId(null);
    }
  };

  const running = sweep?.status === 'running';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[760px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif text-[19px] flex items-center gap-2"><Inbox className="w-4.5 h-4.5" /> Find lost guests</DialogTitle>
        </DialogHeader>
        <div className="text-[12.5px] text-stone-600 -mt-1">
          Scans your Outlook mail (inbox, sent & all folders) for guest leads that never made it into the app. People already in Guests or Prospects are skipped automatically.
        </div>

        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[11px] uppercase text-stone-500">From</Label>
            <Input type="date" data-testid="sweep-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" />
          </div>
          <div>
            <Label className="text-[11px] uppercase text-stone-500">To</Label>
            <Input type="date" data-testid="sweep-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" />
          </div>
          <Button data-testid="sweep-start-btn" onClick={start} disabled={running} className="bg-stone-900 text-white hover:bg-stone-800">
            {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1.5" />}
            {running ? 'Sweeping…' : 'Start sweep'}
          </Button>
        </div>

        {running && (
          <div data-testid="sweep-progress" className="p-3 rounded-md border border-stone-200 bg-stone-50 text-[12.5px] text-stone-700 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-stone-500" />
            {sweep.stage === 'fetching' && 'Fetching emails from Outlook…'}
            {sweep.stage === 'analyzing' && `AI reading correspondence… ${sweep.analyzed || 0}/${sweep.contacts || 0} contacts · ${sweep.found || 0} leads so far`}
            {sweep.stage === 'starting' && 'Starting…'}
          </div>
        )}
        {sweep?.status === 'done' && !candidates.length && (
          <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50 text-[12.5px] text-emerald-900">
            Last sweep ({sweep.dateFrom} → {sweep.dateTo}): scanned {sweep.fetched} emails, no uncaptured leads left to review. 🎉
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {candidates.map(c => (
            <div key={c.id} data-testid={`sweep-candidate-${c.id}`} className="p-3 rounded-md border border-stone-200 hover:border-stone-300 bg-white">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[13.5px] text-stone-900">{c.name}</span>
                    <span className="text-[11.5px] text-stone-500">{c.email}</span>
                    {c.lastDirection === 'inbound'
                      ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">They wrote last</Badge>
                      : <Badge className="bg-stone-100 text-stone-600 hover:bg-stone-100 text-[10px]">You wrote last</Badge>}
                    <span className="text-[10.5px] text-stone-400">{c.threadDate} · {c.msgCount} email{c.msgCount === 1 ? '' : 's'}</span>
                  </div>
                  {c.evidence && <div className="text-[12px] text-stone-600 mt-1 italic">“{c.evidence}”</div>}
                  {c.threadSubject && <div className="text-[11px] text-stone-400 mt-0.5 truncate">Re: {c.threadSubject}</div>}
                </div>
                <button data-testid={`sweep-dismiss-${c.id}`} onClick={() => handle(c, 'dismiss')} disabled={busyId === c.id} className="text-stone-400 hover:text-stone-600 shrink-0" title="Dismiss — never show again">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2 mt-2.5">
                <Button size="sm" variant="outline" data-testid={`sweep-add-prospect-${c.id}`} disabled={busyId === c.id} onClick={() => handle(c, 'prospect')}>
                  <UserPlus className="w-3.5 h-3.5 mr-1" /> Add as Prospect
                </Button>
                <Button size="sm" variant="outline" data-testid={`sweep-add-potential-${c.id}`} disabled={busyId === c.id} onClick={() => handle(c, 'potential')} className="border-violet-300 text-violet-700 hover:bg-violet-50">
                  <Star className="w-3.5 h-3.5 mr-1" /> Potential Guest
                </Button>
                <Button size="sm" data-testid={`sweep-email-${c.id}`} disabled={busyId === c.id} onClick={() => handle(c, 'email')} className="bg-stone-900 text-white hover:bg-stone-800">
                  {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />} Send email
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
